/**
 * Tracks whether the user has been shown — and acknowledged — their
 * recovery phrase.
 *
 * Wallet creation is silent (Exodus-style: phrase generated, encrypted,
 * never shown by default). The Wallet screen shows a persistent banner
 * pushing the user toward `BackupScreen` until they've explicitly
 * revealed the phrase and tapped "I've saved this." That tap sets
 * `acknowledged_at` — banner disappears, the action stays available
 * forever under Wallet → Show recovery phrase.
 *
 * Pure localStorage. Losing the flag (browser data cleared) just means
 * the banner reappears — no risk to funds.
 */

const KEY = "telecrypt.backup-state.v1";

interface BackupState {
  /** ms since epoch; null until the user has confirmed. */
  acknowledged_at: number | null;
}

const EMPTY: BackupState = { acknowledged_at: null };

function load(): BackupState {
  if (typeof localStorage === "undefined") return EMPTY;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<BackupState>;
    return {
      acknowledged_at:
        typeof parsed.acknowledged_at === "number"
          ? parsed.acknowledged_at
          : null,
    };
  } catch {
    return EMPTY;
  }
}

function save(state: BackupState): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // quota / privacy mode — silent; banner just stays around
  }
}

export function isBackedUp(): boolean {
  return load().acknowledged_at != null;
}

export function backedUpAt(): number | null {
  return load().acknowledged_at;
}

/** Idempotent — multiple calls just bump the timestamp. */
export function markBackedUp(): void {
  save({ acknowledged_at: Date.now() });
}

/**
 * Debug / "rotate phrase" use. Resets the flag so the banner returns.
 * Doesn't touch the actual wallet — caller does that separately.
 */
export function resetBackupState(): void {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(KEY);
}
